import { useContext, useState, useEffect } from 'react';
import { Flex, Stack } from '../../shared/layouts';
import Header from '../../shared/Header';
import { useQuery } from 'react-query';
import axios from 'axios';
import Loader from '../../shared/Loader';
import MessagebarContext from '../../../context/MessagebarContext';
import { useTranslation } from 'react-i18next';
import useIconLibrary from '../../../hooks/useIconLibrary';
import Card from '../../shared/Card';
import useForm from '../../../hooks/useForm';
import { useNavigate, useParams } from 'react-router-dom';
import BackButton from '../../shared/BackButton';
import ConfirmAppointmentModal from './components/ConfirmAppointmentModal';
import ConfirmationCard from './components/ConfirmationCard';
import LocationTypeStep from './wizardSteps/LocationTypeStep';
import AppointmentTypeStep from './wizardSteps/AppointmentTypeStep';
import LocationStep from './wizardSteps/LocationStep';
import DateTimeStep from './wizardSteps/DateTimeStep';
import Button from '../../shared/Button';
import {
  appointmentDateFormat,
  formatAppointmentDate
} from '../../../utils/dateHelpers';
import DeleteAppointmentModal from './components/DeleteAppointmentModal';

const CalendarWizard = () => {
  // Hooks
  const { t } = useTranslation();
  const { CalendarIcon } = useIconLibrary();
  const { id } = useParams();
  const navigate = useNavigate();
  const { setSuccesMessage, setAlertMessage } = useContext(MessagebarContext);
  // State
  const [appointment, handleChangeAppointment, setAppointment] = useForm({
    location_type: null,
    partner: false,
    appointment_type_id: null,
    location_id: null,
    start_date: null
  });
  const { appointment_type_id, location_id, location_type, start_date } = appointment;
  const [exisitingAppointment, setExisitingAppointment] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [retrievingData, setRetrievingData] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [disabled, setDisabled] = useState(true);
  const [isFinished, setIsFinished] = useState(false);

  // Data
  const { isLoading, data: appointmentTypes = [] } = useQuery(
    'appointmentTypes',
    async () => {
      try {
        const { data: appointmentTypes } = await axios('/appointment/type');

        return appointmentTypes;
      } catch (error) {
        setAlertMessage();
      }
    },
    { refetchOnWindowFocus: false, retry: false }
  );

  useEffect(() => {
    if (id) {
      setRetrievingData(true);
      axios.get(`/appointment/${id}`).then(({ data }) => {
        const { fullDate } = formatAppointmentDate(data?.start_at?.date);
        const startDate = fullDate.format(appointmentDateFormat);
        const state = {
          location_type: parseInt(data?.appointmenttype?.location_type),
          partner: false,
          appointment_type_id: data?.appointmenttype_id,
          location_id: data?.location_id,
          start_date: startDate
        };
        setAppointment(state);
        setExisitingAppointment(startDate);
        setRetrievingData(false);
      });
    }
  }, [id]);

  const {
    isLoading: datesAreLoading,
    isFetching: datesAreFetching,
    data: availability = {
      dates: [],
      timesByDate: {}
    }
  } = useQuery(
    `availabilityFor${appointment_type_id}At${location_id}`,
    async () => {
      try {
        const { data } = await axios(
          `/appointment/type/${appointment_type_id}/availability?location_id=${location_id ||
            ''}`
        );

        return {
          dates: data?.dates || [],
          timesByDate: data?.date_times || {}
        };
      } catch (error) {
        setAlertMessage();
      }
    },
    {
      refetchOnWindowFocus: false,
      retry: false,
      enabled: appointment_type_id
    }
  );

  // Variables
  const isEditing = !!id;
  const currentAppointment =
    appointmentTypes.find(at => at.id === appointment?.appointment_type_id) ||
    {};
  const availableLocations =
    appointmentTypes.find(at => at?.id === appointment?.appointment_type_id)
      ?.locations || [];
  const locationOptions = availableLocations.map(l => ({
    label: l?.name,
    value: l?.id
  }));
  const currentLocation =
    availableLocations.find(at => at.id === location_id) ||
    null;

  // Wizardlogic
  const steps = isEditing
    ? ['start_date']
    : ['location_type', 'appointment_type_id', 'location_id', 'start_date'];
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const currentStep = steps[currentStepIndex];
  const isLastStep = currentStepIndex === steps.length - 1;
  const submitText = isEditing ? t('Werk afspraak bij') : t('Plan afspraak in');

  const handleNextStep = (goBackwards = false) => {
    let stepAmount = 1;
    const direction = n => (goBackwards ? -Math.abs(n) : n);
    const nextStep = steps[currentStepIndex + direction(stepAmount)];
    const noLocationsAvailable = availableLocations.length === 0;

    if (nextStep === 'location_id' && noLocationsAvailable) {
      stepAmount = 2;
    }

    setDisabled(true);
    setCurrentStepIndex(index => index + direction(stepAmount));
  };

  // Requests
  const handleSubmit = () => {
    setIsSubmitting(true);

    axios({
      method: id ? 'PATCH' : 'POST',
      url: `/appointment/${id || ''}`,
      data: appointment
    })
      .then(() => setIsFinished(true))
      .catch(resp => {
        const errors = resp?.response?.data?.errors;
        setConfirmModalOpen(false);
        setIsSubmitting(false);
        setAlertMessage(typeof errors === 'string' ? errors : null);
      });
  };

  const handleDelete = () => {
    setIsSubmitting(true);

    axios({
      method: 'DELETE',
      url: `/appointment/${id}`,
      data: appointment
    })
      .then(() => {
        setSuccesMessage('De afspraak is verwijderd!');
        navigate('/calendar');
      })
      .catch(() => {
        setDeleteModalOpen(false);
        setIsSubmitting(false);
        setAlertMessage();
      });
  };

  return (
    <>
      {(isLoading || retrievingData) && <Loader />}
      <ConfirmAppointmentModal
        isOpen={confirmModalOpen && !isFinished}
        onRequestClose={() => setConfirmModalOpen(false)}
        handleSubmit={handleSubmit}
        currentAppointment={currentAppointment}
        isSubmitting={isSubmitting}
        isEditing={isEditing}
        appointmentDate={start_date}
      />
      <DeleteAppointmentModal
        isOpen={deleteModalOpen}
        onRequestClose={() => setDeleteModalOpen(false)}
        handleSubmit={handleDelete}
        currentAppointment={currentAppointment}
        isSubmitting={isSubmitting}
        appointmentDate={start_date}
      />
      <Stack space="xl" width={{ sm: 656, ml: 672, lg: 800, xl: 1024 }}>
        <BackButton onClick={() => navigate('/calendar')} />
        <Header
          title={
            isFinished
              ? t('Gelukt! Je afspraak is bevestigd')
              : t('Plan afspraak')
          }
          icon={<CalendarIcon height="2rem" width="2rem" />}
          variant="page"
        />
        <Stack space={'l'}>
          {!isFinished && (
            <Card>
              <Stack space={'l'}>
                {currentStep === 'location_type' && (
                  <LocationTypeStep
                    {...{
                      location_type,
                      appointmentTypes,
                      handleChangeAppointment,
                      setDisabled
                    }}
                  />
                )}
                {currentStep === 'appointment_type_id' && (
                  <AppointmentTypeStep
                    {...{
                      location_type,
                      appointment_type_id,
                      appointmentTypes,
                      handleChangeAppointment,
                      setDisabled
                    }}
                  />
                )}
                {currentStep === 'location_id' && (
                  <LocationStep
                    {...{
                      location_id,
                      locationOptions,
                      handleChangeAppointment,
                      setDisabled
                    }}
                  />
                )}
                {currentStep === 'start_date' && (
                  <DateTimeStep
                    {...{
                      start_date,
                      availability,
                      handleChangeAppointment,
                      setDisabled,
                      isEditing,
                      exisitingAppointment,
                      isLoading:
                        retrievingData || datesAreFetching || datesAreLoading
                    }}
                  />
                )}
                <Flex
                  flexDirection={{ mobile: 'column-reverse', desktop: 'row' }}
                  justifyContent={'flex-end'}
                  css={{ gap: '24px' }}
                >
                  {currentStepIndex > 0 && (
                    <Button
                      variant={'primary-outline'}
                      onClick={() => handleNextStep(true)}
                      width={{ mobile: '100%', desktop: 'auto' }}
                    >
                      {t('Terug')}
                    </Button>
                  )}
                  {!retrievingData && currentStep === 'start_date' && isEditing && (
                    <Button
                      variant={'alert-outline'}
                      onClick={() => setDeleteModalOpen(true)}
                      width={{ mobile: '100%', desktop: 'auto' }}
                    >
                      {t('Verwijder afspraak')}
                    </Button>
                  )}
                  <Button
                    variant={'primary'}
                    disabled={disabled}
                    onClick={() =>
                      isLastStep ? setConfirmModalOpen(true) : handleNextStep()
                    }
                    width={{ mobile: '100%', desktop: 'auto' }}
                  >
                    {isLastStep ? submitText : t('Verder')}
                  </Button>
                </Flex>
              </Stack>
            </Card>
          )}
          {isFinished && (
            <ConfirmationCard
              selectedAppointment={currentAppointment}
              currentLocation={currentLocation}
              startDate={appointment.start_date}
            />
          )}
        </Stack>
      </Stack>
    </>
  );
};

export default CalendarWizard;
